/**
 * NetworkStateSection — the room's OVERALL network state, compact + drill-down.
 *
 * Dynamic context (pool memory) is the cross-run substrate; THIS view answers
 * "what has the network actually been doing / producing?" by reading the
 * execution record (the same Tasks rows the Executions tab shows) and giving the
 * TERMINAL agent's output primacy — the last agent in a run holds the overall
 * context, so its result is the network's effective answer.
 *
 * Two tiers, both from existing APIs (no new backend):
 *   - OVERALL band (always visible): the most recent execution's status, its
 *     terminal agent + that agent's outcome, and an error count across recent runs.
 *   - PER-EXECUTION rows (expand): status + TASK DONE + terminal agent; expanding
 *     a row lazy-loads its executionDetail timeline (per-agent outcome/error/files/PR).
 *
 * Reads:
 *   listNetworkExecutions(networkId, {poolId})           → row list (status, taskLabel, errorCode…)
 *   getNetworkExecution(networkId, taskId)               → executionDetail.timeline (per-agent slots)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronRight, RefreshCw } from 'lucide-react';
import { listNetworkExecutions, getNetworkExecution } from '@/services/networkExecutionsApi';

/** Terminal agent slot = the latest-``ts`` slot in the executionDetail timeline. */
function terminalSlot(executionDetail) {
  const tl = executionDetail && typeof executionDetail === 'object' ? executionDetail.timeline : null;
  const slots = tl && typeof tl === 'object' ? Object.values(tl) : [];
  const valid = slots.filter((s) => s && typeof s === 'object');
  if (!valid.length) return null;
  valid.sort((a, b) => String(a?.ts || '').localeCompare(String(b?.ts || '')));
  return valid[valid.length - 1];
}

/** Did this slot fail? (outcome is absent on legacy/ok slots → treated as ok.) */
function slotFailed(slot) {
  const oc = String(slot?.outcome || '').toLowerCase();
  return oc === 'error' || oc === 'refused';
}

const STATUS_TONE = {
  SUCCEEDED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  FAILED: 'bg-red-50 text-red-700 ring-red-200',
  RUNNING: 'bg-blue-50 text-blue-700 ring-blue-200',
  TIMED_OUT: 'bg-amber-50 text-amber-700 ring-amber-200',
  ABORTED: 'bg-gray-100 text-gray-600 ring-gray-200',
};
function statusChip(status) {
  const s = String(status || '').toUpperCase();
  return STATUS_TONE[s] || 'bg-gray-100 text-gray-600 ring-gray-200';
}

function fmtWhen(iso) {
  const s = String(iso || '').trim();
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** One execution row — collapsed summary; expand to lazy-load the agent timeline. */
function ExecutionRow({ networkId, exec }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const toggle = useCallback(async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail && !loading) {
      setLoading(true);
      setErr(null);
      try {
        const resp = await getNetworkExecution(networkId, exec.taskId);
        setDetail(resp?.executionDetail || null);
      } catch (e) {
        setErr(e?.message || 'Failed to load execution detail');
      } finally {
        setLoading(false);
      }
    }
  }, [open, detail, loading, networkId, exec.taskId]);

  const term = detail ? terminalSlot(detail) : null;
  const slots = useMemo(() => {
    const tl = detail?.timeline;
    const arr = tl && typeof tl === 'object' ? Object.values(tl).filter((s) => s && typeof s === 'object') : [];
    arr.sort((a, b) => String(a?.ts || '').localeCompare(String(b?.ts || '')));
    return arr;
  }, [detail]);

  const label = String(exec.taskLabel || '').trim();
  const summary = String(exec.taskSummary || '').trim();
  const status = String(exec.status || '').toUpperCase();

  return (
    <div className="rounded-md ring-1 ring-gray-100">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-gray-50"
        data-testid={`netstate-exec-${exec.taskId}`}
      >
        <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden />
        <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase ring-1 ${statusChip(status)}`}>
          {status || '—'}
        </span>
        <span className="min-w-0 flex-1 truncate text-[11px] text-gray-700">
          {label || summary || exec.prompt || exec.taskId}
        </span>
        <span className="shrink-0 text-[10px] text-gray-400">{fmtWhen(exec.createdAt)}</span>
      </button>

      {open ? (
        <div className="border-t border-gray-100 px-2.5 py-2" data-testid={`netstate-exec-body-${exec.taskId}`}>
          {/* execution-level error (from the Tasks row) */}
          {exec.errorCause || exec.errorCode ? (
            <div className="mb-2 rounded bg-red-50 px-2 py-1 text-[10px] text-red-700 ring-1 ring-red-100">
              {exec.errorCode ? <span className="font-semibold">{exec.errorCode}: </span> : null}
              {exec.errorCause || 'Execution failed.'}
            </div>
          ) : null}

          {summary ? <p className="mb-2 text-[11px] text-gray-600">{summary}</p> : null}

          {loading ? (
            <div className="text-[10px] text-gray-400">Loading agent timeline…</div>
          ) : err ? (
            <div className="text-[10px] text-red-600">{err}</div>
          ) : slots.length === 0 ? (
            <div className="text-[10px] text-gray-400">No agent timeline recorded for this run.</div>
          ) : (
            <ul className="space-y-1.5">
              {slots.map((s, i) => {
                const isTerm = term && s === term;
                const failed = slotFailed(s);
                return (
                  <li
                    key={s.agent || s.ts || i}
                    className={`rounded px-2 py-1 ring-1 ${isTerm ? 'bg-violet-50 ring-violet-200' : 'bg-gray-50 ring-gray-100'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-[11px] font-medium text-gray-700">
                        {s.agent}
                        {isTerm ? <span className="ml-1 rounded bg-violet-200 px-1 py-px text-[8px] font-semibold uppercase text-violet-800">terminal</span> : null}
                      </span>
                      <span className={`shrink-0 text-[9px] font-semibold uppercase ${failed ? 'text-red-600' : 'text-emerald-600'}`}>
                        {String(s.outcome || 'ok')}
                      </span>
                    </div>
                    {failed && s.error ? (
                      <p className="mt-0.5 text-[10px] text-red-600">{s.error}</p>
                    ) : s.text ? (
                      <p className="mt-0.5 line-clamp-2 text-[10px] text-gray-500">{s.text}</p>
                    ) : null}
                    {Array.isArray(s.files) && s.files.length ? (
                      <p className="mt-0.5 truncate text-[10px] text-gray-400">files: {s.files.slice(0, 6).join(', ')}</p>
                    ) : null}
                    {s.pr && (s.pr.url || s.pr.branch) ? (
                      <p className="mt-0.5 truncate text-[10px] text-blue-600">PR: {s.pr.url || s.pr.branch}</p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function NetworkStateSection({ open, networkId, poolId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!networkId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await listNetworkExecutions(networkId, { poolId, limit: 10 });
      setRows(Array.isArray(resp?.executions) ? resp.executions : []);
    } catch (e) {
      setError(e?.message || 'Failed to load network state');
    } finally {
      setLoading(false);
    }
  }, [networkId, poolId]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  // Overall band: most recent run + error count across the loaded window.
  const last = rows[0] || null;
  const errorCount = useMemo(
    () => rows.filter((r) => String(r.status || '').toUpperCase() === 'FAILED').length,
    [rows],
  );

  return (
    <section data-testid="network-state" className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold text-indigo-700">Network state</h3>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="inline-flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-600 disabled:opacity-50"
          aria-label="Refresh network state"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} aria-hidden />
        </button>
      </div>
      <p className="text-[10px] text-gray-400">
        What the network has actually produced — recent executions and the terminal
        agent's outcome (the last agent holds the overall context).
      </p>

      {loading && !rows.length ? (
        <div className="text-[11px] text-gray-500">Loading…</div>
      ) : error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700" role="alert">{error}</div>
      ) : !rows.length ? (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-4 text-center text-[11px] text-gray-500">
          No executions in this room yet.
        </div>
      ) : (
        <>
          {/* OVERALL band */}
          {last ? (
            <div className="rounded-md bg-indigo-50 px-3 py-2 ring-1 ring-indigo-100" data-testid="network-state-overall">
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase ring-1 ${statusChip(last.status)}`}>
                  {String(last.status || '—').toUpperCase()}
                </span>
                <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-gray-800">
                  {String(last.taskLabel || last.taskSummary || 'Most recent run')}
                </span>
                <span className="shrink-0 text-[10px] text-gray-400">{fmtWhen(last.createdAt)}</span>
              </div>
              <div className="mt-1 flex items-center gap-2 text-[10px] text-gray-500">
                <span>{rows.length} recent run{rows.length === 1 ? '' : 's'}</span>
                {errorCount > 0 ? (
                  <span className="rounded bg-red-100 px-1.5 py-px font-semibold text-red-700">{errorCount} failed</span>
                ) : (
                  <span className="rounded bg-emerald-100 px-1.5 py-px font-semibold text-emerald-700">0 failed</span>
                )}
              </div>
            </div>
          ) : null}

          {/* PER-EXECUTION rows */}
          <div className="space-y-1.5">
            {rows.map((exec) => (
              <ExecutionRow key={exec.taskId} networkId={networkId} exec={exec} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
